import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { RequestService } from '../request';
import { WINDOW } from '../window.providers';

export const SSO_ENDPOINT = 'sso/login';
export const SSO_SUPPORT_URL = 'mailto:support@adclarity.com';

export interface SsoErrorResponse {
    sso_error?: string;
    redirect_url?: string;
}

@Injectable({ providedIn: 'root' })
export class SsoLoginService {
    private readonly requestService = inject(RequestService);
    private readonly window = inject<Window>(WINDOW);

    /**
     * Probes SSO availability for the given email.
     *
     * GET /rest/v2/sso/login?email=...
     * - Success (302 redirect / 2xx): returns null
     * - Fail (JSON body with { sso_error }): returns the sso_error code
     */
    probeSSO(email: string): Observable<string | null> {
        return this.requestService
            .get<SsoErrorResponse>(`${SSO_ENDPOINT}?email=${encodeURIComponent(email)}`, { observe: 'response' })
            .pipe(
                map((response: HttpResponse<SsoErrorResponse>): string | null => {
                    if (response.status === 200) {
                        const redirectUrl = response.body?.redirect_url;

                        if (redirectUrl) {
                            this.window.location.href = redirectUrl;
                            return null;
                        }

                        return 'UNKNOWN_SSO_ERROR';
                    }

                    return 'UNKNOWN_SSO_ERROR';
                }),
                catchError((error: HttpErrorResponse | unknown) => {
                    if (error instanceof HttpErrorResponse) {
                        const body =
                            error.error && typeof error.error === 'object'
                                ? (error.error as SsoErrorResponse)
                                : undefined;

                        const code = body?.sso_error || String(error.status || 'UNKNOWN_SSO_ERROR');

                        return of(code);
                    }

                    return of('UNKNOWN_SSO_ERROR');
                }),
            );
    }
}
